/**
 * Pure CSS-gradient generator for the Custom Gradient builder.
 *
 * WHY THIS EXISTS
 * ----------------
 * The wallpaper picker stores gradient presets as raw CSS `background` strings in
 * `wallpaper_settings.bg_wallpaper` (currentBg === 3). The render path applies
 * that string DIRECTLY as a CSS `background` value, so any valid CSS gradient
 * renders instantly with ZERO network requests (see constants/wallpaperGradients.js
 * for the same contract used by the curated preset library).
 *
 * This module lets a user COMPOSE their own gradient instead of only picking a
 * preset. The output is the exact same kind of CSS string the presets already
 * produce, so it persists through the unchanged `handleGradientColor(string)`
 * path with NO contract change, NO backend change, NO new field.
 *
 * Everything here is PURE (no React, no DOM, no I/O) so it is trivially testable:
 * `buildGradientCss(state) -> string`. `parseGradientCss(string) -> state | null`
 * is the best-effort inverse for editing an existing custom gradient.
 */

/**
 * Maximum number of color stops a user can add. Tunable — the UI enforces this
 * cap (add button disables at the limit) and the generator is unbounded so a
 * caller can lift the cap without touching the math.
 */
export const MAX_GRADIENT_STOPS = 6;

/** Minimum number of color stops — a gradient needs at least two colors. */
export const MIN_GRADIENT_STOPS = 2;

/** Supported gradient types (CSS-valid). */
export const GRADIENT_TYPES = ["linear", "radial", "conic"];

/** Quick linear-direction presets -> angle in degrees. */
export const LINEAR_DIRECTION_PRESETS = [
  { id: "vertical", label: "Vertical", angle: 180 }, // top -> bottom
  { id: "horizontal", label: "Horizontal", angle: 90 }, // left -> right
  { id: "diagonal", label: "Diagonal", angle: 135 }, // top-left -> bottom-right
];

/** Radial shapes (CSS-valid). */
export const RADIAL_SHAPES = ["circle", "ellipse"];

/**
 * Named positions usable by radial (`at <pos>`) and conic (`at <pos>`). These
 * map to the CSS `<position>` keywords so the output is always valid CSS.
 */
export const GRADIENT_POSITIONS = [
  { id: "center", label: "Center", css: "center" },
  { id: "top", label: "Top", css: "top" },
  { id: "bottom", label: "Bottom", css: "bottom" },
  { id: "left", label: "Left", css: "left" },
  { id: "right", label: "Right", css: "right" },
  { id: "top left", label: "Top Left", css: "top left" },
  { id: "top right", label: "Top Right", css: "top right" },
  { id: "bottom left", label: "Bottom Left", css: "bottom left" },
  { id: "bottom right", label: "Bottom Right", css: "bottom right" },
];

const POSITION_IDS = new Set(GRADIENT_POSITIONS.map((p) => p.id));

/** Clamp a number into [min, max], coercing non-finite values to `min`. */
const clamp = (n, min, max) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.min(max, Math.max(min, x));
};

/** Normalise an angle to an integer in [0, 360]. */
export const clampAngle = (angle) => Math.round(clamp(angle, 0, 360));

/** Normalise a stop position % to an integer in [0, 100]. */
export const clampPosition = (pos) => Math.round(clamp(pos, 0, 100));

/* -------------------------------------------------------------------------- */
/* Color helpers — used by the draggable stop-track to INTERPOLATE a color     */
/* when a user clicks an empty spot on the gradient bar (so the inserted stop  */
/* visually matches the gradient at that point). Pure, no DOM, hex-only path   */
/* with a graceful fallback: anything we can't parse returns null and the      */
/* caller falls back to the nearest stop's raw color string (rgb()/named/etc). */
/* -------------------------------------------------------------------------- */

/**
 * Parse a `#rgb` / `#rrggbb` hex color into `{ r, g, b }` (0–255), or null if
 * the string isn't a plain hex color (callers then fall back to copying the
 * nearest stop's color verbatim — never crashing on rgb()/named colors).
 */
export const parseHexColor = (color) => {
  if (typeof color !== "string") return null;
  const m = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

/** Serialise `{ r, g, b }` (0–255) back to an uppercase `#rrggbb` hex string. */
export const rgbToHex = ({ r, g, b }) => {
  const h = (n) =>
    Math.round(clamp(n, 0, 255)).toString(16).padStart(2, "0").toUpperCase();
  return `#${h(r)}${h(g)}${h(b)}`;
};

/**
 * Interpolate the color of the gradient at a given position % from its stops.
 *
 * Finds the two stops bracketing `position` and linearly blends them in RGB.
 * If `position` falls outside the stop range, returns the nearest stop's color.
 * If either bracketing stop isn't a plain hex color, returns the nearest stop's
 * color VERBATIM (so rgb()/rgba()/named colors are preserved, not mangled).
 *
 * @param {Array<{color:string,position:number}>} stops the current stops
 * @param {number} position target position % (0–100)
 * @returns {string} a color string suitable for a new stop
 */
export const interpolateColorAt = (stops, position) => {
  const list = (Array.isArray(stops) ? stops.filter(Boolean) : [])
    .map((s) => ({ color: s.color, position: clampPosition(s.position) }))
    .sort((a, b) => a.position - b.position);
  if (list.length === 0) return "#FFFFFF";
  const pos = clampPosition(position);

  // Clamp to the ends.
  if (pos <= list[0].position) return list[0].color;
  if (pos >= list[list.length - 1].position) {
    return list[list.length - 1].color;
  }

  // Find the bracketing pair.
  let lo = list[0];
  let hi = list[list.length - 1];
  for (let i = 0; i < list.length - 1; i += 1) {
    if (pos >= list[i].position && pos <= list[i + 1].position) {
      lo = list[i];
      hi = list[i + 1];
      break;
    }
  }

  const a = parseHexColor(lo.color);
  const b = parseHexColor(hi.color);
  // Fall back to the nearer stop's raw color if either side isn't hex.
  if (!a || !b) {
    const mid = (lo.position + hi.position) / 2;
    return pos < mid ? lo.color : hi.color;
  }

  const span = hi.position - lo.position || 1;
  const t = (pos - lo.position) / span;
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
};

/**
 * The canonical default builder state: a 2-stop horizontal linear gradient.
 * Used as the initial UI state and as the fallback when a parse fails.
 */
export const defaultGradientState = () => ({
  type: "linear",
  angle: 90,
  shape: "circle", // radial only
  position: "center", // radial + conic
  fromAngle: 0, // conic only
  stops: [
    { color: "#FF9A9E", position: 0 },
    { color: "#FAD0C4", position: 100 },
  ],
});

/**
 * Safe fallback color used when a stop's color value is missing or would break
 * out of the CSS gradient context (injection / malformed input).
 */
const SAFE_DEFAULT_COLOR = "#000000";

/**
 * Whitelist of CSS color FUNCTIONS we accept verbatim. Anything using `(` that
 * is NOT one of these is rejected, which blocks `url(...)` and arbitrary
 * function calls smuggled in through a color value.
 */
const COLOR_FN = /^(?:rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch|color)\(/i;

/**
 * Validate a CSS color value for safe inlining into a gradient string.
 *
 * Rejects values containing CSS-breaking control chars (`;`, `{`, `}`, `<`,
 * `>`) that could close the declaration / rule and inject styles, and rejects
 * any `(` that is not the opening of a whitelisted color function (which blocks
 * `url(...)` and unknown functions). Preserves valid hex / named / rgb / rgba /
 * hsl / oklch (and other modern color-function) values unchanged.
 *
 * @param {*} color candidate color value
 * @returns {boolean} true when the value is safe to inline
 */
export const isSafeColor = (color) => {
  if (typeof color !== "string") return false;
  const c = color.trim();
  if (!c) return false;
  // Hard-reject CSS context-breaking characters and quotes/backslashes.
  if (/[;{}<>"'\\]/.test(c)) return false;
  // Any parenthesis must open a whitelisted color function and stay balanced.
  if (c.includes("(")) {
    if (!COLOR_FN.test(c)) return false;
    if (!c.endsWith(")")) return false;
    let depth = 0;
    for (const ch of c) {
      if (ch === "(") depth += 1;
      else if (ch === ")") depth -= 1;
      // A top-level (depth 0) comma means two values smuggled into one stop
      // (e.g. "rgb(1,2,3), red") — reject; commas inside the fn are fine.
      else if (ch === "," && depth === 0) return false;
      if (depth < 0) return false;
    }
    if (depth !== 0) return false;
  } else if (c.includes(")") || c.includes(",")) {
    // a stray `)` (break-out) or a top-level comma (two values) in a
    // parenless value (e.g. a hex or a bare "red, blue") is unsafe.
    return false;
  }
  return true;
};

/**
 * Coerce a color to a safe value for building: valid colors pass through,
 * anything unsafe falls back to {@link SAFE_DEFAULT_COLOR}.
 */
const safeColor = (color) => (isSafeColor(color) ? color.trim() : SAFE_DEFAULT_COLOR);

/**
 * Serialise a single color stop to `"<color> <pos>%"`. Position is optional in
 * CSS, but we always emit it so the output round-trips cleanly.
 */
const stopToCss = (stop) =>
  `${safeColor(stop.color)} ${clampPosition(stop.position)}%`;

/**
 * Build a valid CSS gradient string from builder state.
 *
 * Guarantees:
 *  - Always returns a syntactically valid `linear|radial|conic-gradient(...)`.
 *  - Never throws on partial/garbage input — missing fields fall back to the
 *    defaults, stops below the minimum are padded from the default state.
 *  - Angles clamp to [0,360], stop positions clamp to [0,100].
 *
 * @param {object} state builder state (see defaultGradientState)
 * @returns {string} CSS background string
 */
export const buildGradientCss = (state) => {
  const s = { ...defaultGradientState(), ...(state || {}) };

  // Guarantee at least MIN_GRADIENT_STOPS valid stops.
  let stops = Array.isArray(s.stops) ? s.stops.filter(Boolean) : [];
  if (stops.length < MIN_GRADIENT_STOPS) {
    stops = defaultGradientState().stops;
  }
  const stopsCss = stops.map(stopToCss).join(", ");

  const type = GRADIENT_TYPES.includes(s.type) ? s.type : "linear";

  if (type === "radial") {
    const shape = RADIAL_SHAPES.includes(s.shape) ? s.shape : "circle";
    const position = POSITION_IDS.has(s.position) ? s.position : "center";
    return `radial-gradient(${shape} at ${position}, ${stopsCss})`;
  }

  if (type === "conic") {
    const fromAngle = clampAngle(s.fromAngle);
    const position = POSITION_IDS.has(s.position) ? s.position : "center";
    return `conic-gradient(from ${fromAngle}deg at ${position}, ${stopsCss})`;
  }

  // linear
  const angle = clampAngle(s.angle);
  return `linear-gradient(${angle}deg, ${stopsCss})`;
};

/* -------------------------------------------------------------------------- */
/* Parse-back (best-effort): CSS string -> builder state, for EDITING an       */
/* existing custom gradient. Returns null if the string isn't a gradient this  */
/* builder produced (callers then fall back to create-only with defaults).     */
/* -------------------------------------------------------------------------- */

/**
 * Split a gradient's inner argument list on top-level commas only (so commas
 * inside `rgb(...)` / `rgba(...)` color functions don't split a stop).
 */
const splitTopLevel = (inner) => {
  const parts = [];
  let depth = 0;
  let current = "";
  for (const ch of inner) {
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (ch === "," && depth === 0) {
      parts.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
};

/** Parse one `"<color> <pos>%"` token into a stop, or null if malformed. */
const parseStop = (token) => {
  const t = token.trim();
  const m = t.match(/^(.*?)(?:\s+(-?\d+(?:\.\d+)?)%)?$/);
  if (!m || !m[1]) return null;
  const color = m[1].trim();
  if (!color) return null;
  // Skip stops whose color would break out of the CSS gradient context.
  if (!isSafeColor(color)) return null;
  const position = m[2] != null ? clampPosition(m[2]) : null;
  return { color, position };
};

/**
 * Best-effort inverse of buildGradientCss. Handles the linear/radial/conic
 * shapes this builder emits (and tolerates the curated-library variants:
 * `<angle>deg`, `circle|ellipse [at <pos>]`, `from <a>deg [at <pos>]`).
 * Returns a full builder state on success, or null on anything it can't map.
 */
export const parseGradientCss = (css) => {
  if (typeof css !== "string") return null;
  const str = css.trim();
  const match = str.match(/^(linear|radial|conic)-gradient\((.*)\)$/is);
  if (!match) return null;

  const type = match[1].toLowerCase();
  const inner = match[2];
  const parts = splitTopLevel(inner);
  if (parts.length === 0) return null;

  const base = defaultGradientState();
  let stopParts = parts;
  const out = { ...base, type };

  if (type === "linear") {
    // Optional leading "<angle>deg" or "to <side>".
    const head = parts[0];
    const angleMatch = head.match(/^(-?\d+(?:\.\d+)?)deg$/i);
    if (angleMatch) {
      out.angle = clampAngle(angleMatch[1]);
      stopParts = parts.slice(1);
    } else if (/^to\s+/i.test(head)) {
      // "to <side>" form: map common cases, else leave default angle.
      out.angle = sideKeywordToAngle(head) ?? base.angle;
      stopParts = parts.slice(1);
    }
  } else if (type === "radial") {
    // Optional leading "circle|ellipse [at <pos>]" or "at <pos>".
    const head = parts[0];
    if (/circle|ellipse|\bat\b/i.test(head) && !/%/.test(head)) {
      const shapeM = head.match(/\b(circle|ellipse)\b/i);
      if (shapeM) out.shape = shapeM[1].toLowerCase();
      const posM = head.match(/\bat\s+(.+)$/i);
      if (posM && POSITION_IDS.has(posM[1].trim().toLowerCase())) {
        out.position = posM[1].trim().toLowerCase();
      }
      stopParts = parts.slice(1);
    }
  } else if (type === "conic") {
    // Optional leading "from <a>deg [at <pos>]".
    const head = parts[0];
    if (/\bfrom\b|\bat\b/i.test(head)) {
      const fromM = head.match(/from\s+(-?\d+(?:\.\d+)?)deg/i);
      if (fromM) out.fromAngle = clampAngle(fromM[1]);
      const posM = head.match(/\bat\s+(.+)$/i);
      if (posM && POSITION_IDS.has(posM[1].trim().toLowerCase())) {
        out.position = posM[1].trim().toLowerCase();
      }
      stopParts = parts.slice(1);
    }
  }

  // Fail closed: if ANY stop is malformed/unsafe, reject the whole parse rather
  // than silently dropping it (which would corrupt the user's gradient on edit).
  const parsed = stopParts.map(parseStop);
  if (parsed.some((s) => !s)) return null;
  const stops = parsed;
  if (stops.length < MIN_GRADIENT_STOPS) return null;

  // Fill any missing positions by even distribution so the UI always has a %.
  const n = stops.length;
  out.stops = stops.map((stop, i) => ({
    color: stop.color,
    position:
      stop.position != null
        ? stop.position
        : Math.round((i / (n - 1)) * 100),
  }));

  return out;
};

/** Map the common `to <side>` linear keywords to an angle. */
const SIDE_ANGLES = {
  "to top": 0,
  "to right": 90,
  "to bottom": 180,
  "to left": 270,
  "to top right": 45,
  "to bottom right": 135,
  "to bottom left": 225,
  "to top left": 315,
};
const sideKeywordToAngle = (head) => {
  const key = head.trim().toLowerCase().replace(/\s+/g, " ");
  return SIDE_ANGLES[key] ?? null;
};

/* -------------------------------------------------------------------------- */
/* RENDERER-V2 ADAPTER: CSS gradient string -> STRUCTURED gradient spec.        */
/*                                                                             */
/* The renderer-v2 WEDGE fill painter (src/renderer-v2/fills.js buildFillPaint) */
/* paints a REAL SVG gradient — <linearGradient>/<radialGradient> in <defs>, or */
/* a clipped conic foreignObject — from this shape:                            */
/*   { kind:'linear'|'radial'|'conic',                                          */
/*     stops:[{ offset:0..1, color }],                                          */
/*     angleDeg? (linear + conic),                                              */
/*     position?:{ x, y } 0..1 (radial + conic center) }                        */
/*                                                                             */
/* This is the SAME CSS-gradient format buildGradientCss (the custom builder)   */
/* AND the curated wallpaperGradients library produce — INCLUDING the curated   */
/* library's percentage positions (`at 30% 20%`), which parseGradientCss (the   */
/* builder's named-position editing inverse) does not decode. cssGradientToSpec */
/* is the ONE parser the renderer uses so the wedge gradient can never silently  */
/* diverge from the format this module owns.                                    */
/*                                                                             */
/* Fail-closed: returns null for a non-gradient / <2-stop / any unsafe-color    */
/* string, so the caller falls back to a solid wedge (never renders garbage).   */
/* Colors are gated by the SAME isSafeColor used on build, so a color that       */
/* flows into the conic foreignObject CSS can never break out of context.       */
/* -------------------------------------------------------------------------- */

/** Named CSS position keyword -> the axis fraction (0..1) it pins. */
const POS_AXIS = {
  left: { x: 0 },
  right: { x: 1 },
  top: { y: 0 },
  bottom: { y: 1 },
};

/**
 * Normalise a CSS `<position>` (named keywords and/or percentages) into
 * `{ x, y }` objectBoundingBox fractions (0..1). Returns null on an unknown
 * token (caller then defaults to center). Handles: "center", "top left",
 * "30% 20%", "50% 0%", single "30%" (y -> center), single "top" (x -> center).
 */
const normalizePosition = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;
  let x = null;
  let y = null;
  for (const tok of s.split(/\s+/)) {
    const pm = tok.match(/^(-?\d+(?:\.\d+)?)%$/);
    if (pm) {
      const v = clamp(pm[1], 0, 100) / 100;
      if (x == null) x = v;
      else if (y == null) y = v;
      continue;
    }
    if (tok === "center") continue; // resolved by the center defaults below
    const axis = POS_AXIS[tok];
    if (!axis) return null; // unknown keyword -> bail so caller centers it
    if ("x" in axis) x = axis.x;
    if ("y" in axis) y = axis.y;
  }
  return { x: x == null ? 0.5 : x, y: y == null ? 0.5 : y };
};

/** Parse one "<color> [<pos>%]" stop token -> { offset:0..1, color } or null. */
const parseStopOffset = (token, index, count) => {
  const t = String(token || "").trim();
  const m = t.match(/^(.*?)(?:\s+(-?\d+(?:\.\d+)?)%)?$/);
  if (!m || !m[1]) return null;
  const color = m[1].trim();
  if (!color || !isSafeColor(color)) return null;
  const offset =
    m[2] != null ? clamp(m[2], 0, 100) / 100 : count > 1 ? index / (count - 1) : 0;
  return { offset, color };
};

/**
 * Parse a CSS gradient string into the renderer-v2 structured gradient spec (see
 * the block comment above). Returns null for anything it can't safely render.
 *
 * @param {string} css a `linear|radial|conic-gradient(...)` string
 * @returns {{kind:string, stops:Array<{offset:number,color:string}>, angleDeg?:number, position?:{x:number,y:number}}|null}
 */
export const cssGradientToSpec = (css) => {
  if (typeof css !== "string") return null;
  const str = css.trim();
  const match = str.match(/^(linear|radial|conic)-gradient\(([\s\S]*)\)$/i);
  if (!match) return null;
  const kind = match[1].toLowerCase();
  const parts = splitTopLevel(match[2]);
  if (parts.length === 0) return null;

  let angleDeg;
  let position;
  let stopParts = parts;
  const head = parts[0];

  if (kind === "linear") {
    const angleM = head.match(/^(-?\d+(?:\.\d+)?)deg$/i);
    if (angleM) {
      angleDeg = clampAngle(angleM[1]);
      stopParts = parts.slice(1);
    } else if (/^to\s+/i.test(head)) {
      angleDeg = sideKeywordToAngle(head) ?? 180;
      stopParts = parts.slice(1);
    } else {
      angleDeg = 180; // CSS default linear direction = "to bottom"
    }
  } else if (kind === "radial") {
    // An orientation head starts with a shape / size / `at` keyword — never a
    // color — so this can't misread a color stop as orientation.
    if (
      /^\s*(circle|ellipse|closest-side|closest-corner|farthest-side|farthest-corner|at\b)/i.test(
        head
      )
    ) {
      const posM = head.match(/\bat\s+(.+)$/i);
      if (posM) position = normalizePosition(posM[1]) || undefined;
      stopParts = parts.slice(1);
    }
  } else if (kind === "conic") {
    if (/^\s*(from\b|at\b)/i.test(head)) {
      const fromM = head.match(/\bfrom\s+(-?\d+(?:\.\d+)?)deg/i);
      angleDeg = fromM ? clampAngle(fromM[1]) : 0;
      const posM = head.match(/\bat\s+(.+)$/i);
      if (posM) position = normalizePosition(posM[1]) || undefined;
      stopParts = parts.slice(1);
    } else {
      angleDeg = 0;
    }
  }

  const n = stopParts.length;
  if (n < MIN_GRADIENT_STOPS) return null;
  const stops = [];
  for (let i = 0; i < n; i += 1) {
    const stop = parseStopOffset(stopParts[i], i, n);
    if (!stop) return null; // fail closed on ANY malformed / unsafe stop
    stops.push(stop);
  }

  const spec = { kind, stops };
  if (angleDeg != null) spec.angleDeg = angleDeg;
  if (position) spec.position = position;
  return spec;
};
